import {IWedEdBar, IWedEditorMain, OWedEditorMainConfig, WedMgr} from "back/edit/wed/wedEditor";
import {findWedEditor, IWedlet, WEDLET} from "back/edit/wed/wedlets/wedlet";
import {isWedletSingleElt, IWedletSingleElt, OffView} from "back/edit/wed/wedlets/wedletSingleElt";
import {JSX, ShadowJsx} from "lib/commons/xml/dom";
import {XA} from "lib/commons/xml/xAddr";
import {EAnnotLevel, ISkAnnot, SkAnnotAttrUnknown, SkAnnotEltUnknown, SkAnnotTextForbidden} from "lib/edit/schema/schemaAnnots";
import {REG} from "lib/commons/registry";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {GFX} from "lib/commons/utils/gfx";
import {BoxHiddenWedlet} from "back/edit/wed/wedlets/box/box";


/** Barre verticale d'affichage d'erreur. ATTENTION, exige un IDocHolderSync. */
export interface IWedEditorBoxErrBar extends IWedEditorMain {
	errBar: IBoxErrBar;
}

export interface OWedEditorErrBarConfig extends OWedEditorMainConfig {
	hideErrBar?: boolean;

	/** N'affichera pas la barre globale "unknownBar" si des élements inconnus sont présents. Utile que si hideErrBar!==true. */
	hideUnknownBar?: boolean;
}

export function AgBoxErrBarEditor(cls: any): any {
	const proto = cls.prototype as IWedEditorBoxErrBar;
	const superInit = proto.initialize;
	proto.initialize = function (this: IWedEditorBoxErrBar, config: OWedEditorErrBarConfig) {
		if (config.hideErrBar) {
			superInit.call(this, config);
		} else {
			superInit.call(this, config);
			this.errBar = new BoxErrBar().init(this, config);
			this.barLayout.showBar(this.errBar);
		}
		return this;
	};
	return cls;
}

export interface IBoxErrBar extends IWedEdBar {
}

export class BoxErrBar extends HTMLElement implements IBoxErrBar {

	wedEditor: IWedEditorBoxErrBar;

	container: HTMLElement;

	unknownBarShown?: HTMLElement | null;

	constructor() {
		super();
		this.id = "errorBar";
	}

	// TODO box-errorbar stylable
	init(wedEditor: IWedEditorBoxErrBar, config: OWedEditorErrBarConfig): this {
		this.wedEditor = wedEditor;

		if (!config.hideUnknownBar) this.unknownBarShown = null; //Marque que cette barr peut être affichée.

		const style = this.style;
		style.width = ".8em";
		style.display = "flex";
		style.flexDirection = "column";
		this.container = this.appendChild(<div/>);
		const ctnStyle = this.container.style;
		ctnStyle.flexShrink = "1";
		ctnStyle.position = "relative";
		this.clear();

		wedEditor.wedMgr.listeners.on("asyncSkAnnots", onSkAnnots);
		this.wedEditor.rootNode.addEventListener("wed-resized", () => {
			const wedMgr = this.wedEditor.wedMgr;
			Promise.resolve().then(() => {onSkAnnots(wedMgr, wedMgr.lastSkAnnots)});
		});
		return this;
	}

	clear() {
		this.container.textContent = null;
	}

	buildUnknownBar() {
		this.unknownBarShown = <div id="unknownBar">
			<ShadowJsx skin="box-errbar/unknownBar" reg={this.wedEditor.wedMgr.reg}>
				<span>Des éléments inconnus sont présents...</span>
				<c-button label="Supprimer" onclick={this.delUnknowns} title="Supprime tous les éléments inconnus." ui-context="dialog"/>
			</ShadowJsx>
		</div>;
	}

	delUnknowns(this: Button) {
		const editor = findWedEditor(this) as IWedEditorBoxErrBar;
		const wedMgr = editor.wedMgr;
		if (wedMgr.readOnly) {
			POPUP.showNotifForbidden("Modification interdite", this);
			return;
		}
		const batch = wedMgr.docHolder.newBatch();
		const annots = wedMgr.docHolder.getAnnots(wedMgr.rootWedlet.wedAnchor);
		for (const annot of annots) {
			if (annot.type === SkAnnotEltUnknown.TYPE || annot.type === SkAnnotTextForbidden.TYPE) {
				batch.deleteSequence(annot.start, 1).needAdjustForNextAdds();
			} else if (annot.type === SkAnnotAttrUnknown.TYPE) {
				batch.setAttr(annot.start, null).needAdjustForNextAdds();
			}
		}
		batch.doBatch();
	}
}


REG.reg.registerSkin('box-errbar/unknownBar', 1, /* language=CSS */ `
	:host {
		color: var(--error-color);
		text-align: center;
	}

	c-button {
		display: inline-flex;
	}
`);

window.customElements.define("box-errbar", BoxErrBar);

//TODO revoir algo:
// - une seule annot par mark (le cas où plusieurs annots sont vraiment sur le même noeud est rare, envisager une annot container)
// - class js simples pour ErrBarMark(annot, offsetTop) et ErrBarCtn(wedlet, annots, offsetTop, height).
// - layout flex avec empilement des marks, container de marks (wed-offview) et div flex d'espacement
// - dans le new tableau des annots, on remplace les annots par les ErrBarMark ou ErrBarCtn qui wrap les annnots et fixe l'offsetTop
//   en fonction du wedlet associé.
// - l'accumulation des annots dans  le même ErrBarCtn est simple car les annots sont ordonnés dans l'ordre naturel des noeuds,
//   on peut donc retrouver le ErrBarCtn précédent, vérifier qu'il opointe le même wedlet, y ajouter l'annot,
//   et mettre null à l'offset dans le tableau.
// - on trie le tableau selon l'offsetTop (gestion de cas particuliers où l'organisation en hauteur ne correspond pas à l'ordre naturel, colonnage...)
// - on parcours les annots déjà dessinés qu'on compare à ceux du tableau par rapport à l'offsetTop et le title (demain un level ?)
//   et on met à jour et injecte les diff (à la React).


function onSkAnnots(wedMgr: WedMgr, annots: ISkAnnot[], deadline?: RequestIdleDeadline): void | ((wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline) => void) {
	if (deadline?.timeRemaining() < 5) return onSkAnnots;
	const errBar = (wedMgr.wedEditor as IWedEditorBoxErrBar).errBar as BoxErrBar;
	const docHolder = wedMgr.docHolder;
	if (!docHolder || !docHolder.isAvailable) return;

	const rootWedlet = wedMgr.rootWedlet;
	let rootTop: number;
	let rootHeight: number;
	let totalPercent: number;
	if (isWedletSingleElt(rootWedlet)) {
		const rootRect = rootWedlet.element.getBoundingClientRect();
		rootTop = rootRect.top;
		rootHeight = rootWedlet.element.scrollHeight;
		totalPercent = rootHeight / 100;
	} else {
		errBar.clear();
		return;
	}

	// if(annots.length > 100) {
	// 	//TODO Mode d'affichage avec trop d'erreurs...
	//  return;
	// }

	const map = new Map<IWedletSingleElt, ISkAnnot[]>();
	const marks = [] as Array<ErrBarMark | ErrBarCtn>;
	let countUnknown = 0;
	for (let i = 0; i < annots.length; i++) {
		const annot = annots[i];
		if (annot.level.weight < 0 || annot.result === null) continue;
		let wedlet: IWedlet;
		if ("anchor" in annot) {//Ancrage, pas sur un noeud, mais dans un interstice (Missing et diffForeign annots)
			//Cas particulier pour le placement d'un noeud manquant ou étranger.
			let start = annot.start;
			wedlet = WEDLET.findWedlet(rootWedlet, start);
			if (!wedlet && annot.offset > 0) {
				start = XA.incrAtDepth(start, -1, -1);
				wedlet = WEDLET.findWedlet(rootWedlet, start);
				if (isWedletSingleElt(wedlet)) {
					const rect = GFX.getContextBoundingRect(wedlet.element);
					if (rect) marks.push(new ErrBarMark().init([annot], Math.trunc((rect.top + rect.height - rootTop) / totalPercent)));
					continue;
				}
			}
			if (!wedlet) wedlet = WEDLET.findWedlet(rootWedlet, XA.up(start), WEDLET.FINDOPTIONS_lastAncestorIfNone);
		} else {
			wedlet = WEDLET.findWedlet(rootWedlet, annot.anchor || annot.start, WEDLET.FINDOPTIONS_lastAncestorIfNone);
		}
		if (wedlet instanceof BoxHiddenWedlet) continue; //on masque les erreurs des BoxHidden.
		while (wedlet && !isWedletSingleElt(wedlet)) wedlet = wedlet.wedParent;
		if (!wedlet) continue;
		const prevAnnot = map.get(wedlet as IWedletSingleElt);
		if (!prevAnnot) {
			map.set(wedlet as IWedletSingleElt, [annot]);
		} else {
			prevAnnot.push(annot);
		}
		if (annot.type === SkAnnotEltUnknown.TYPE || annot.type === SkAnnotAttrUnknown.TYPE || annot.type === SkAnnotTextForbidden.TYPE) {
			countUnknown++;
		}
	}

	let prevTopPercent = -1;
	let prevMark: ErrBarMark;

	map.forEach((annots: ISkAnnot[], wedlet: IWedletSingleElt) => {
		const rect = GFX.getContextBoundingRect(wedlet.element);
		if (!rect) return;
		const topPercent = Math.trunc((rect.top - rootTop) / totalPercent);
		if (annots.length > 1 && wedlet.element instanceof OffView) {
			const ctn = new ErrBarCtn().init(topPercent, Math.trunc(rect.height / totalPercent));
			for (let i = 0; i < annots.length; i++) {
				const annot = annots[i];
				if (!annot || annot.result === null) continue;
				const grp = [annot];
				for (let k = i + 1; k < annots.length; k++) {
					const other = annots[k];
					if (other && other.anchorNode === annot.anchorNode) {
						grp.push(other);
						annots[k] = null;
					}
				}
				ctn.appendChild(new ErrBarMark().init(grp));
			}
			marks.push(ctn);
		} else {
			if (prevTopPercent === topPercent) {
				prevMark.addAnnots(annots);
			} else {
				prevTopPercent = topPercent;
				prevMark = new ErrBarMark().init(annots, topPercent);
				marks.push(prevMark);
			}
		}
	});

	//TODO trier marks selon topPercent pour traiter ceux qui ont le même topPercent et gérer un affichage non superposé.

	//console.log("remaintime after computing", deadline.timeRemaining());
	errBar.clear();
	errBar.container.style.height = rootHeight + "px";
	for (let i = 0; i < marks.length; i++) {
		errBar.container.appendChild(marks[i]);
	}

	// Gestion de la barre des éléments inconnus.
	if ('unknownBarShown' in errBar) {
		if (countUnknown > 0) {
			if (!errBar.unknownBarShown) {
				errBar.buildUnknownBar();
				errBar.wedEditor.barLayout.showBar(errBar.unknownBarShown);
			}
		} else {
			if (errBar.unknownBarShown) {
				errBar.wedEditor.barLayout.removeBar(errBar.unknownBarShown);
				errBar.unknownBarShown = null;
			}
		}
	}

	//console.log("remaintime after redraw", deadline.timeRemaining());
}

class ErrBarMark extends HTMLElement {
	topPercent?: number;
	annots: ISkAnnot[];

	init(annots: ISkAnnot[], topPercent?: number): this {
		this.annots = annots;
		if (topPercent != null) {
			this.topPercent = topPercent;
			this.style.position = "absolute";
			this.style.top = topPercent + "%";
		}
		this.style.height = "3px";
		this.style.left = "0";
		this.style.right = "0";
		this.style.cursor = "pointer";
		this.addEventListener("click", this._onClick);
		this.computeAnnots(annots);
		this.redraw();
		return this;
	}

	addAnnots(annots: ISkAnnot[]) {
		this.annots.push(...annots);
		this.computeAnnots(annots);
		this.redraw();
	}

	protected computeAnnots(annots: ISkAnnot[]) {
		let callRedraw: () => void;
		for (const a of annots) {
			if (a.future) a.future.then(callRedraw || (callRedraw = this.redraw.bind(this)));
		}
	}

	protected redraw() {
		let level = EAnnotLevel.info;
		let ti: string;
		let count = 0;
		let countMore = 0;
		const maxLabels = 5;
		this.annots.sort((a1, a2) => !a1 && !a2 ? a1 ? -1 : a2 ? 1 : 0 : a2.level.weight - a1.level.weight); //Plus grave en 1er.
		for (let i = 0; i < this.annots.length; i++) {
			const a = this.annots[i];
			if (a && a.result !== null) { //a non null et non résolu à null.
				level = level.max(a.level);
				if (++count < maxLabels) {
					if (ti) ti += "\n" + a.getLabel();
					else ti = a.getLabel();
				} else {
					countMore++;
				}
			}
		}
		if (count === 0) {
			if (this.parentNode?.parentNode && this.parentNode.localName === "errbar-ctn" && !this.nextSibling && !this.previousSibling) {
				this.parentElement.remove();
			} else {
				this.remove();
			}
		} else {
			if (countMore > 0) ti += `\n(et ${countMore} autres)`;
			this.style.backgroundColor = levelColors[level.weight];
			this.setAttribute("title", ti);
		}
	}

	protected async _onClick(ev: MouseEvent) {
		const annot = this.annots.find((a) => a && !a.future && !a.result !== null) || this.annots.find((a) => a != null);
		WEDLET.focusAnnot(findWedEditor(this).wedMgr, annot);
	}
}

const levelColors = ['white', 'var(--fade-color)', 'var(--warning-color)', 'var(--error-color)', 'var(--edit-diff-color)', 'var(--edit-diffrem-color)', 'var(--edit-diffadd-color)', 'var(--edit-search-bgcolor)'];

window.customElements.define("errbar-mark", ErrBarMark);

/** Container de marks pour les box off-view qu'on ne peut placer précisément. */
class ErrBarCtn extends HTMLElement {
	topPercent: number;
	heightPercent: number;

	init(topPercent: number, heightPercent: number): this {
		this.topPercent = topPercent;
		this.heightPercent = heightPercent;
		this.style.position = "absolute";
		this.style.top = topPercent + "%";
		this.style.height = heightPercent > 0 ? heightPercent + "%" : "3px";
		this.style.left = "0";
		this.style.right = "0";
		this.style.display = "flex";
		this.style.flexDirection = "column";
		this.style.justifyContent = "space-around";
		return this;
	}
}

window.customElements.define("errbar-ctn", ErrBarCtn);